// src/views/UserViews/insights/components/FirmwareVersionCurrency.tsx
//
// Replaces the old per-version ranked-list `VersionDistributionChart` (deleted) with the same
// simplified currency-bar pattern `MobileVersionCurrency` already uses: one summary bar split
// into up-to-date/needs-update, not a ranked list of every firmware string. Firmware has no
// per-platform (iOS/Android-equivalent) split, so this renders exactly ONE fleet-wide bar,
// not one per `is_core` class (Compass Core/Pro) - see `computeFirmwareCurrency`'s header
// comment for why it is not group-aware the way `computePlatformCurrency` is. No top summary
// line: unlike mobile (whose iOS/Android split isn't otherwise visible anywhere in this
// card), firmware's one total is already implicit in the legend's per-tier device counts
// below, so a separate line would just repeat it.
//
// Two footnotes below the bar, each independent of `latestVersion` availability so they stay
// visible even while the release catalog is loading/failed:
//   - Bootloader-parked devices (`BOOTLOADER_FIRMWARE_VERSION` sentinel) - a real, actionable
//     failure signal (failed/interrupted DFU), never folded into "needs update" since that
//     undersells the urgency of a device that cannot progress on its own.
//   - Unknown-firmware devices (`UNKNOWN_VERSION_LABEL` sentinel, null/empty `c_fw`) - reports
//     telemetry but no firmware string, distinct from mobile's "unrecognized platform"
//     footnote (`INSIGHTS_VERSION_UNKNOWN_PLATFORM_FOOTNOTE`), whose copy is about an
//     unrecognized phone model/app version and would read wrong here.
//
// SCOPING (current): both footnotes above are hidden from this card by explicit product
// request - only "Needs update" should be visible on the Insights surface for now. This does
// NOT reverse the reasoning two paragraphs up (bootloader still IS a real, actionable signal
// that still shouldn't be folded into "needs update") - it overrides where that signal is
// currently surfaced, not whether it's real. Gated behind `SHOW_BOOTLOADER_FOOTNOTE`/
// `SHOW_UNKNOWN_FOOTNOTE` below (both `false`) rather than deleted: this is a reversible
// "computed but not rendered" flag, kept in the source alongside the render logic it gates
// rather than commented out or deleted, so re-enabling either footnote is a one-line flip,
// not a re-implementation. `currency.bootloaderCount`/`unknownCount` are still computed by
// `computeFirmwareCurrency` every render (untouched, still unit-tested on their own) - only
// the two `SHOW_*` flags below need to change to restore either footnote.

import { useMemo, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { VersionBucket } from '@/models/Insights/InsightsTypes';
import { computeFirmwareCurrency } from '@/models/Insights/versionCurrency';
import SectionCard from './SectionCard';
import FirmwareDetailsDialog from './FirmwareDetailsDialog';

const LOADING_SKELETON_HEIGHT = 320;
const PERCENT_MULTIPLIER = 100;
const BAR_HEIGHT = 8;
const BAR_BORDER_RADIUS = 999;

type FirmwareTier = 'upToDate' | 'needsUpdate';

/** Rendering order for the bar's segments and legend rows - up to date first, then needs
 * update, matching severity (same convention as `MobileVersionCurrency`'s `TIER_ORDER`). */
const TIER_ORDER: FirmwareTier[] = ['upToDate', 'needsUpdate'];

/** Same palette convention as `MobileVersionCurrency`'s (unexported) `TIER_COLOR` - success
 * for up to date, warning for needs update. Not imported: that file's map also carries an
 * `unsupported` entry firmware has no use for, and isn't exported. Duplicating two color
 * values isn't worth a shared module for two call sites. */
const TIER_COLOR: Record<FirmwareTier, string> = {
  upToDate: 'success.main',
  needsUpdate: 'warning.main',
};

const TIER_LABEL_KEY: Record<FirmwareTier, string> = {
  upToDate: StringKeys.INSIGHTS_VERSION_UP_TO_DATE,
  needsUpdate: StringKeys.INSIGHTS_VERSION_NEEDS_UPDATE,
};

// SCOPING flags (see header comment's "SCOPING (current)" paragraph for the full why) -
// currently `false`: only "Needs update" is visible on this card. `currency.bootloaderCount`/
// `unknownCount` are still computed unconditionally below regardless of these flags; only
// their rendering is gated, so flipping either back to `true` is a one-line change, not a
// re-implementation.
const SHOW_BOOTLOADER_FOOTNOTE = false;
const SHOW_UNKNOWN_FOOTNOTE = false;

/** Same small square swatch as `MobileVersionCurrency`'s (unexported) `LegendSwatch`. */
function LegendSwatch({ color }: { color: string }) {
  return <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />;
}

type FirmwareVersionCurrencyProps = {
  buckets: VersionBucket[];
  loading?: boolean;
  error?: Error | null;
  /** Single "latest published version" value from the release catalog
   * (`FirmwareLookupTableOnAzure.latest_firmware_version`). Callers must not pass a value
   * while the catalog is loading/failed (see `Insights.tsx`) - `null`/empty renders no bar
   * (bootloader/unknown footnotes are independent of `latestVersion` for the same reason -
   * they're not currency judgements - though per this file's "SCOPING (current)" header
   * comment, both are currently hidden regardless of `latestVersion` anyway). */
  latestVersion?: string | null;
  /** Org code for the "View details" dialog's own lazy row-level query
   * (`getFirmwareDeviceDetails`) - unrelated to `buckets`/`latestVersion` above, which come
   * from the card's own aggregate `getVersionDistribution` call. Defaults to `''` so
   * existing callers/tests that never open the dialog are unaffected. */
  orgCode?: string;
};

export default function FirmwareVersionCurrency({
  buckets,
  loading = false,
  error = null,
  latestVersion = null,
  orgCode = '',
}: FirmwareVersionCurrencyProps) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const currency = useMemo(
    () => computeFirmwareCurrency(buckets, latestVersion),
    [buckets, latestVersion],
  );

  const countByTier: Record<FirmwareTier, number> = {
    upToDate: currency.upToDate,
    needsUpdate: currency.needsUpdate,
  };

  return (
    <SectionCard
      title={t(StringKeys.INSIGHTS_FIRMWARE_VERSION_CHART_TITLE)}
      headingLevel="h3"
      action={
        <Button
          size="small"
          onClick={() => setDetailsOpen(true)}
          data-testid="firmware-view-details-button"
        >
          {t(StringKeys.INSIGHTS_FIRMWARE_VIEW_DETAILS_ACTION)}
        </Button>
      }
    >
      {/* Visible whenever `latestVersion` is known, independent of the buckets'
          loading/error state below - `Insights.tsx` already nulls `latestVersion` while the
          release catalog itself is loading/failed, so this line disappears in that case for
          the same reason the bar's tiers do, without needing its own loading/error gate. */}
      {latestVersion && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="firmware-current-version"
          sx={{ mb: 1.5 }}
        >
          {t(StringKeys.INSIGHTS_FIRMWARE_CURRENT_VERSION_LABEL, { version: latestVersion })}
        </Typography>
      )}

      {loading && (
        <Skeleton
          variant="rectangular"
          height={LOADING_SKELETON_HEIGHT}
          data-testid="version-distribution-loading"
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid="version-distribution-error">
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && (
        <>
          {currency.total > 0 && (
            <Box data-testid="firmware-version-currency-bar" sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  height: BAR_HEIGHT,
                  borderRadius: BAR_BORDER_RADIUS,
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                }}
              >
                {TIER_ORDER.map(
                  (tier) =>
                    countByTier[tier] > 0 && (
                      <Box
                        key={tier}
                        sx={{ flexGrow: countByTier[tier], bgcolor: TIER_COLOR[tier] }}
                      />
                    ),
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.75 }}>
                {TIER_ORDER.filter((tier) => countByTier[tier] > 0).map((tier) => {
                  const percent = Math.round(
                    (countByTier[tier] / currency.total) * PERCENT_MULTIPLIER,
                  );
                  return (
                    <Box key={tier} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LegendSwatch color={TIER_COLOR[tier]} />
                      <Typography variant="caption" color="text.secondary">
                        {t(TIER_LABEL_KEY[tier], { deviceCount: countByTier[tier], percent })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {SHOW_BOOTLOADER_FOOTNOTE && currency.bootloaderCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="version-firmware-bootloader-footnote"
              sx={{ display: 'block', mt: 1 }}
            >
              {t(StringKeys.INSIGHTS_FIRMWARE_BOOTLOADER_FOOTNOTE, {
                deviceCount: currency.bootloaderCount,
              })}
            </Typography>
          )}

          {SHOW_UNKNOWN_FOOTNOTE && currency.unknownCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="version-firmware-unknown-footnote"
              sx={{ display: 'block', mt: 1 }}
            >
              {t(StringKeys.INSIGHTS_FIRMWARE_UNKNOWN_FOOTNOTE, {
                deviceCount: currency.unknownCount,
              })}
            </Typography>
          )}
        </>
      )}

      <FirmwareDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        orgCode={orgCode}
        latestVersion={latestVersion}
      />
    </SectionCard>
  );
}
